import {mapObject} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {Nullable} from 'data-types/benchling-type-def/ComplexTypes';
import {
  FormDefinitionContent,
  FormDefinitionField,
  FormDefinitionSection,
  FormSectionType,
  FORM_FIELD_TYPE_TO_BNCH_TYPE_DEF,
} from 'forms/form-designer/Constants';

import {
  FormInstanceFieldState,
  FormInstanceSectionState,
  FormInstanceState,
  FormSubmissionFieldValue,
  FormSubmissionSectionValue,
  FormSubmissionValues,
} from './Constants';

export type FormInstanceStatePath = (formInstanceState: FormInstanceState) => FormInstanceState;

export function makeFormInstanceState(
  contentDefinitions: Array<FormDefinitionSection | FormDefinitionField>,
  existingState?: FormInstanceState
): FormInstanceState {
  let formState: FormInstanceState = {};
  for (const sectionOrField of contentDefinitions) {
    const key = sectionOrField.key;
    if (sectionOrField.type === 'FormSection') {
      const section = sectionOrField;
      // if the section is iterable, add a section to the submission layer
      if (section.sectionType === FormSectionType.ITERABLE) {
        const existingSectionState = existingState?.[key] as FormInstanceSectionState | undefined;
        formState[key] = existingSectionState
          ? {
              type: 'section',
              // keep the same number of iterations of the iterable
              items: existingSectionState.items.map((sectionStateIterable) =>
                makeFormInstanceState(section.content, sectionStateIterable)
              ),
            }
          : {
              type: 'section',
              // start with one instance of the iterated content by default
              items: [makeFormInstanceState(section.content)],
            };
        // if the section is static, flatten the section by surfacing all content to the current submission
      } else {
        formState = {...formState, ...makeFormInstanceState(section.content, existingState)};
      }
      // if it's a field, add it directly to the submission
    } else {
      const field = sectionOrField;
      const existingFieldState = existingState?.[key] as FormInstanceFieldState | undefined;
      const freshFieldState = formDefinitionFieldToFieldState(field);
      formState[key] = existingFieldState
        ? {
            // carry over the existing field value
            ...freshFieldState,
            value: existingFieldState.value,
            isValidValue: existingFieldState.isValidValue,
          }
        : freshFieldState;
    }
  }
  return formState;
}

/** Initializes the Form Instance State from the Definition.
 * Optionally can include an existing state to attempt to preserve information from like existing field values.
 */
export function initializeFormInstanceState(
  definition: FormDefinitionContent,
  existingState?: FormInstanceState
): FormInstanceState {
  return makeFormInstanceState(definition.sections, existingState);
}

/** Traverse the FormInstanceState and check whether all fields have a valid value */
export function validateFormInstanceStateFields(state: FormInstanceState): boolean {
  return Object.values(state).reduce((isValid, fieldOrSectionState) => {
    if (fieldOrSectionState.type === 'field') {
      const fieldState = fieldOrSectionState;
      return isValid && fieldState.isValidValue;
    } else {
      const sectionState = fieldOrSectionState;
      return isValid && sectionState.items.every((s) => validateFormInstanceStateFields(s));
    }
  }, true);
}

export function convertFormInstanceStateToFormSubmission(state: FormInstanceState): FormSubmissionValues {
  return mapObject(state, (fieldOrSectionState) => {
    if (fieldOrSectionState.type === 'field') {
      const {label, fieldType, value, isRequired} = fieldOrSectionState;
      const fieldSubmission: FormSubmissionFieldValue = {
        label,
        value,
        isRequired,
        fieldType: fieldType.toString(),
      };
      return fieldSubmission;
    } else {
      const {items} = fieldOrSectionState;
      const sectionSubmission: FormSubmissionSectionValue = {
        items: items.map((item) => convertFormInstanceStateToFormSubmission(item)),
      };
      return sectionSubmission;
    }
  });
}

/**
 * Converts a FormDefinitionField to a FormInstanceFieldInput, filling extra fields in with their defaults
 */
export function formDefinitionFieldToFieldState(
  formDefinitionField: FormDefinitionField
): FormInstanceFieldState {
  const {key, label, description, isRequired, schemaId, schemaFieldSelectorId} = formDefinitionField;

  const benchlingTypeDef = FORM_FIELD_TYPE_TO_BNCH_TYPE_DEF[formDefinitionField.fieldType];

  assert(
    Object.values(LeafType).includes(benchlingTypeDef),
    `For now types must be single layer types, not nested. ${benchlingTypeDef}`
  );

  // TODO: BNCH-50539 update benchling type value input to allow null boolean state
  let defaultValue = null;
  if (benchlingTypeDef) {
    defaultValue = isRequired;
    if (benchlingTypeDef === LeafType.DROPDOWN_OPTION) {
      defaultValue = {schemaFieldSelectorOptionIds: [], value: ''};
    }
  }
  const fieldType = isRequired ? benchlingTypeDef : new Nullable(benchlingTypeDef);

  return {
    type: 'field',
    fieldType,
    key,
    label,
    description,
    isRequired,
    schemaId,
    schemaFieldSelectorId,
    value: fieldType === LeafType.DROPDOWN_OPTION ? [] : defaultValue,
    isValidValue: !isRequired || benchlingTypeDef === LeafType.BOOLEAN,
  };
}
